import { Accessibility, Type, Contrast, Zap, BookA, Minus, Plus, VolumeX } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useAppMode } from "@/context/AppModeContext";
import { stopSpeak } from "@/lib/voice";

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className="flex items-center gap-2.5 text-sm font-medium"><Icon className="w-4 h-4 text-primary" />{label}</span>
      {children}
    </div>
  );
}

export default function AccessibilityToolbar() {
  const { a11y, updateA11y } = useAppMode();
  const scale = a11y.textScale || 1;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="a11y-toggle"
          aria-label="Accessibility options"
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 grid place-items-center hover:scale-105 active:scale-95 transition-transform focus:outline-none focus:ring-4 focus:ring-primary/40"
        >
          <Accessibility className="w-6 h-6" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="top" className="w-80 rounded-lg" data-testid="a11y-panel">
        <h3 className="font-head font-bold mb-1">Accessibility</h3>
        <p className="text-xs text-muted-foreground mb-3">Adjust anything, anytime.</p>

        <Row icon={Type} label="Text size">
          <div className="flex items-center gap-2">
            <button data-testid="text-smaller" onClick={() => updateA11y({ textScale: Math.max(0.9, +(scale - 0.1).toFixed(2)) })} className="h-8 w-8 grid place-items-center rounded-md border border-border hover:border-primary"><Minus className="w-4 h-4" /></button>
            <span className="w-10 text-center text-sm font-bold tabular-nums">{Math.round(scale * 100)}%</span>
            <button data-testid="text-bigger" onClick={() => updateA11y({ textScale: Math.min(1.6, +(scale + 0.1).toFixed(2)) })} className="h-8 w-8 grid place-items-center rounded-md border border-border hover:border-primary"><Plus className="w-4 h-4" /></button>
          </div>
        </Row>
        <Row icon={Contrast} label="High contrast">
          <Switch data-testid="toggle-contrast" checked={a11y.highContrast} onCheckedChange={(v) => updateA11y({ highContrast: v })} />
        </Row>
        <Row icon={Zap} label="Reduce motion">
          <Switch data-testid="toggle-motion" checked={a11y.reduceMotion} onCheckedChange={(v) => updateA11y({ reduceMotion: v })} />
        </Row>
        <Row icon={BookA} label="Dyslexia-friendly">
          <Switch data-testid="toggle-dyslexia" checked={a11y.dyslexia} onCheckedChange={(v) => updateA11y({ dyslexia: v })} />
        </Row>
        <button
          onClick={stopSpeak}
          data-testid="stop-speaking"
          className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border py-2 text-sm font-semibold hover:bg-secondary"
        >
          <VolumeX className="w-4 h-4" /> Stop reading aloud
        </button>
      </PopoverContent>
    </Popover>
  );
}
